import React from 'react'
import { motion } from 'framer-motion'
import { Smartphone, Heart, Github, Twitter, Coffee } from 'lucide-react'
import { Link } from 'react-router-dom'
import logo from '../../assets/images/logo.png'
import { trackDonationClick } from '../common/GoogleAnalytics'

const Footer = () => {
  const currentYear = new Date().getFullYear()

  const links = {
    product: [
      { name: 'Editor', href: '#editor' },
      { name: 'Features', href: '#features' },
      { name: 'Instructions', href: '#instructions' },
      { name: 'Support Us', href: '/support-us' },
    ],
    resources: [
      { name: 'iOS Guidelines', href: 'https://developer.apple.com/design/human-interface-guidelines/app-icons' },
      { name: 'Android Guidelines', href: 'https://developer.android.com/guide/practices/ui_guidelines/icon_design' },
    ],
    legal: [
      { name: 'Privacy Policy', to: '/privacy-policy', isRouterLink: true },
      { name: 'Terms of Service', to: '/terms-of-service', isRouterLink: true },
      { name: 'Cookie Policy', to: '/cookie-policy', isRouterLink: true },
      { name: 'DPA', to: '/dpa', isRouterLink: true },
    ]
  }

  const socialLinks = [
    // { name: 'GitHub', href: '#', icon: Github },
    // { name: 'Twitter', href: '#', icon: Twitter },
  ]

  return (
    <footer className="bg-gray-900 text-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-0 py-12">
        <div className="flex flex-col lg:flex-row lg:justify-between gap-8">
          {/* Brand Section - Left */}
          <div className="lg:max-w-sm">
            <motion.div
              className="flex items-center space-x-2 mb-4"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              viewport={{ once: true }}
            >
              <div className="p-1 bg-[#df6e57] rounded-lg">
              <img 
                src={logo} 
                alt="EasyAppIcon Logo" 
                className="w-8 h-8"
              />
              </div>
              <span className="text-xl font-bold">EasyAppIcon</span>
            </motion.div>
            
            <motion.p
              className="text-gray-400 mb-6 max-w-md leading-relaxed"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.1 }}
              viewport={{ once: true }}
            >
              The modern, fast, and secure way to generate app icons for iOS and Android. 
              Created with ❤️ for developers who value quality and simplicity.
            </motion.p>

            {/* Support Button */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.2 }}
              viewport={{ once: true }}
            >
              {/* Donation Button */}
              <div className="flex justify-start">
                <a 
                  href="https://www.buymeacoffee.com/dg43aLHr6" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  onClick={() => trackDonationClick()}
                  className="hover:scale-105 transition-transform duration-200"
                >
                  <img 
                    src="https://cdn.buymeacoffee.com/buttons/v2/default-yellow.png" 
                    alt="Buy Me A Coffee" 
                    className="h-10 w-auto"
                  />
                </a>
              </div>
            </motion.div>
          </div>

          {/* Links Sections - Right */}
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-x-12 gap-y-8">
            {Object.entries(links).map(([category, categoryLinks], index) => (
              <motion.div
                key={category}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.1 * (index + 1) }}
                viewport={{ once: true }}
              >
                <h3 className="text-white font-semibold mb-4 capitalize">
                  {category === 'legal' ? 'Legal' : category}
                </h3>
                <ul className="space-y-3">
                  {categoryLinks.map((link) => (
                    <li key={link.name}>
                      {link.isRouterLink ? (
                        <Link
                          to={link.to}
                          className="text-gray-400 hover:text-white transition-colors duration-200 text-sm"
                        >
                          {link.name}
                        </Link>
                      ) : (
                        <a
                          href={link.href}
                          className="text-gray-400 hover:text-white transition-colors duration-200 text-sm"
                        >
                          {link.name}
                        </a>
                      )}
                    </li>
                  ))}
                </ul>
              </motion.div>
            ))}
          </div>
        </div>

        {/* Bottom Section */}
        <div className="border-t border-gray-800 mt-12 pt-8">
          <div className="flex flex-col md:flex-row justify-between items-center space-y-4 md:space-y-0">
            {/* Copyright */}
            <motion.div
              className="text-gray-400 text-sm"
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              transition={{ duration: 0.5 }}
              viewport={{ once: true }}
            >
              © {currentYear} EasyAppIcon. Built by{' '}
              <a
                href="https://easyio.dev"
                target="_blank"
                rel="noopener noreferrer"
                className="hover:text-white transition-colors duration-200"
              >
                EasyIO.dev
              </a>
              .
            </motion.div>

            {/* Social Links */}
            <motion.div
              className="flex items-center space-x-4"
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              transition={{ duration: 0.5, delay: 0.1 }}
              viewport={{ once: true }}
            >
              {socialLinks.map((social) => (
                <a
                  key={social.name}
                  href={social.href}
                  className="text-gray-400 hover:text-white transition-colors duration-200"
                  aria-label={social.name}
                >
                  <social.icon className="w-5 h-5" />
                </a>
              ))}
            </motion.div>

            {/* Made with Love */}
            <motion.div
              className="flex items-center space-x-1 text-gray-400 text-sm"
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              transition={{ duration: 0.5, delay: 0.2 }}
              viewport={{ once: true }}
            >
              <span>Made with</span>
              <Heart className="w-4 h-4 text-red-500" />
              <span>for developers</span>
            </motion.div>
          </div>
        </div>
      </div>
    </footer>
  )
}

export default Footer 
